import { forwardRef } from 'react';
import {
	Button as ChakraButton,
	IconButton as ChakraIconButton,
	type ButtonProps as ChakraButtonProps,
	type IconButtonProps as ChakraIconButtonProps,
} from '@chakra-ui/react';

/**
 * Button primitive.
 *
 * Contract:
 * - `variant`: 'solid' | 'outline' | 'outlineGray' | 'outlineSubtle' | 'ghost' | 'ghostGray' | 'link'
 * - `colorScheme`: defaults to 'brand' (Apple Blue). Status actions use
 *   'red' | 'green' | 'orange'. Never pass inline `bg`/`color` hex.
 * - All color comes from theme recipes (see src/ui/theme/components/button.ts).
 *   Neutral (non-brand) looks are variants there — `outlineGray`, `outlineSubtle`,
 *   `ghostGray` — not call-site `color`/`_hover` overrides.
 *
 * Replaces the legacy `PartBayButton`.
 */
export type ButtonVariant = 'solid' | 'outline' | 'outlineGray' | 'outlineSubtle' | 'ghost' | 'ghostGray' | 'link';

export interface ButtonProps extends Omit<ChakraButtonProps, 'variant'> {
	variant?: ButtonVariant;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
	({ variant = 'solid', colorScheme = 'brand', ...rest }, ref) => (
		<ChakraButton ref={ref} variant={variant} colorScheme={colorScheme} {...rest} />
	),
);
Button.displayName = 'Button';

export interface IconButtonProps extends Omit<ChakraIconButtonProps, 'variant'> {
	variant?: ButtonVariant;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
	({ variant = 'ghost', colorScheme = 'brand', ...rest }, ref) => (
		<ChakraIconButton ref={ref} variant={variant} colorScheme={colorScheme} {...rest} />
	),
);
IconButton.displayName = 'IconButton';

export default Button;
